import React, { useState, useEffect } from 'react';
import './Roulette.css';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IoClose } from 'react-icons/io5';
import toast from 'react-hot-toast';
import { authAPI, userAPI } from '../../services/api';
import { login as loginThunk, register as registerThunk } from '../../Features/Auth/authSlice';
import { fetchRouletteConfig } from '../../Features/Roulette/rouletteSlice';

const Roulette = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { parts, loading: configLoading } = useSelector((state) => state.roulette);

    const [isOpen, setIsOpen] = useState(false);
    const [isSpinning, setIsSpinning] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [hasSpun, setHasSpun] = useState(() => localStorage.getItem('lellahom_roulette_v2_spun') === 'true');
    const [wonCode, setWonCode] = useState(() => localStorage.getItem('lellahom_roulette_code') || '');
    const [result, setResult] = useState(() => localStorage.getItem('lellahom_roulette_prize') || null);

    const [showAuthForm, setShowAuthForm] = useState(false);
    const [authMode, setAuthMode] = useState('register');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [authLoading, setAuthLoading] = useState(false);

    // Derived segments from API parts
    const segments = parts.length > 0 ? parts.map((part, index) => ({
        text: part.value,
        color: index % 3 === 0 ? '#f8f9fa' : index % 3 === 1 ? '#c8a96e' : '#1a1a1a'
    })) : [
        { text: '...', color: '#f8f9fa' },
        { text: '...', color: '#c8a96e' },
        { text: '...', color: '#1a1a1a' },
        { text: '...', color: '#f8f9fa' },
        { text: '...', color: '#c8a96e' },
        { text: '...', color: '#1a1a1a' },
    ];

    const [hasSpunThisSession, setHasSpunThisSession] = useState(false);

    useEffect(() => {
        if (user) {
            // If user object has a promo code, they already won.
            if (user.promo_code) {
                setHasSpun(true);
                setWonCode(user.promo_code);
                setResult('Cadeau Exclusif');
                localStorage.setItem('lellahom_roulette_v2_spun', 'true');
                localStorage.setItem('lellahom_roulette_code', user.promo_code);
                localStorage.setItem('lellahom_roulette_prize', 'Cadeau Exclusif');
            } else if (!hasSpunThisSession) {
                // If they are logged in, have NO promo code on their account,
                // and they haven't spun during this session, clear any old local storage data.
                setHasSpun(false);
                setWonCode('');
                setResult(null);
                localStorage.removeItem('lellahom_roulette_v2_spun');
                localStorage.removeItem('lellahom_roulette_code');
                localStorage.removeItem('lellahom_roulette_prize');
            }
        }
    }, [user, hasSpunThisSession]);

    useEffect(() => {
        dispatch(fetchRouletteConfig());

        // Show when dispatched from anywhere (e.g. at Checkout)
        const handleOpenRoulette = () => {
            setIsOpen(true);
        };

        window.addEventListener('open-roulette', handleOpenRoulette);

        return () => {
            window.removeEventListener('open-roulette', handleOpenRoulette);
        };
    }, [dispatch]);

    // Show auth form directly if they open the roulette without being logged in
    useEffect(() => {
        if (isOpen && !user) {
            setShowAuthForm(true);
        } else if (isOpen && user) {
            setShowAuthForm(false);
        }
    }, [isOpen, user]);

    const generatePromoCode = () => {
        const randomNumber = Math.floor(1000 + Math.random() * 9000);
        return `LLH-${randomNumber}`;
    };

    const handleAuth = async (e) => {
        e.preventDefault();
        setAuthLoading(true);
        try {
            if (authMode === 'register') {
                const nameParts = name.trim().split(' ');
                const userData = {
                    first_name: nameParts[0] || 'User',
                    last_name: nameParts.slice(1).join(' ') || '.',
                    email,
                    password,
                    phone: phone || '00000000',
                    address: address || '.'
                };

                const resultAction = await dispatch(registerThunk(userData));
                if (registerThunk.fulfilled.match(resultAction)) {
                    toast.success('Compte créé ! Veuillez vous connecter.');
                    setAuthMode('login');
                } else {
                    throw new Error(resultAction.payload || 'Registration failed');
                }
            } else {
                const resultAction = await dispatch(loginThunk({ email, password }));
                if (loginThunk.fulfilled.match(resultAction)) {
                    setShowAuthForm(false);
                    toast.success('Bienvenue ! Vous pouvez maintenant lancer la roue.');
                } else {
                    throw new Error(resultAction.payload || 'Login failed');
                }
            }
        } catch (error) {
            toast.error(error.message || 'Erreur lors de l’authentification');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleSpin = () => {
        if (!user) {
            setShowAuthForm(true);
            return;
        }

        if (isSpinning || hasSpun) return;

        setIsSpinning(true);

        const extraDegrees = Math.floor(Math.random() * 360);
        const totalRotation = rotation + (360 * 5) + extraDegrees;
        setRotation(totalRotation);

        setTimeout(() => {
            setIsSpinning(false);
            setHasSpun(true);
            setHasSpunThisSession(true);

            const normalizedDegrees = (totalRotation % 360);
            const segmentAngle = 360 / segments.length;
            const segmentIndex = Math.floor(((360 - normalizedDegrees + (segmentAngle / 2)) % 360) / segmentAngle);
            const won = segments[segmentIndex].text;
            setResult(won);

            const code = generatePromoCode();
            setWonCode(code);

            localStorage.setItem('lellahom_roulette_v2_spun', 'true');
            localStorage.setItem('lellahom_roulette_prize', won);
            localStorage.setItem('lellahom_roulette_code', code);

            // Call to API to persist it for the user if they're logged in and won a discount
            if (user && won && !won.toLowerCase().includes('hard luck') && !won.toLowerCase().includes('0%')) {
                // Try updating the user's promo code in the database directly
                userAPI.update(user.id, { promo_code: code })
                    .then(() => {
                        console.log("Promo code saved to user successfully.");
                    })
                    .catch(err => {
                        console.error("Failed to update promo code remotely:", err);
                    });
            }

            toast.success(`Félicitations ! Code : ${code}`, {
                duration: 6000,
                icon: '🎁',
            });
        }, 4000);
    };

    return (
        <>
            {!isOpen && (
                <div className="floating-roulette-btn" onClick={() => setIsOpen(true)}>
                    <div className="floating-roulette-wheel">
                        <div className="floating-roulette-center"></div>
                    </div>
                </div>
            )}

            {isOpen && (
                <div className="roulette-overlay">
                    <div className="roulette-modal">
                        <button className="roulette-close" onClick={() => setIsOpen(false)}>
                            <IoClose size={24} />
                        </button>

                        {showAuthForm ? (
                            <div className="roulette-auth-container">
                                <h3>{authMode === 'register' ? 'Créer un compte' : 'Se connecter'}</h3>
                                <p>Inscrivez-vous pour sauvegarder votre cadeau !</p>
                                <form onSubmit={handleAuth}>
                                    {authMode === 'register' && (
                                        <>
                                            <input
                                                className="roulette-input"
                                                placeholder="Nom complet"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                required
                                            />
                                            <input
                                                className="roulette-input"
                                                placeholder="Téléphone"
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                required
                                            />
                                            <input
                                                className="roulette-input"
                                                placeholder="Adresse"
                                                value={address}
                                                onChange={(e) => setAddress(e.target.value)}
                                                required
                                            />
                                        </>
                                    )}
                                    <input
                                        className="roulette-input"
                                        type="email"
                                        placeholder="Email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                    <input
                                        className="roulette-input"
                                        type="password"
                                        placeholder="Mot de passe"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    <button className="roulette-auth-btn" type="submit" disabled={authLoading}>
                                        {authLoading ? '...' : (authMode === 'register' ? 'S’inscrire' : 'Se connecter')}
                                    </button>
                                </form>
                                <p className="roulette-auth-toggle">
                                    {authMode === 'register' ? 'Déjà un compte ? ' : 'Pas de compte ? '}
                                    <span onClick={() => setAuthMode(authMode === 'register' ? 'login' : 'register')}>
                                        {authMode === 'register' ? 'Se connecter' : 'S’inscrire'}
                                    </span>
                                </p>
                                <button className="roulette-close" onClick={() => setShowAuthForm(false)} style={{ marginTop: '10px', position: 'static' }}>Retour à la roue</button>
                            </div>
                        ) : (
                            <>
                                <div className="roulette-header">
                                    <h2>Tentez votre chance !</h2>
                                    <p>Faites tourner la roue pour gagner des cadeaux exclusifs.</p>
                                </div>

                                <div className="roulette-container">
                                    <div className="roulette-lights">
                                        {[...Array(12)].map((_, i) => (
                                            <div key={i} className="light" style={{ transform: `rotate(${i * 30}deg) translateY(-175px)` }}></div>
                                        ))}
                                    </div>
                                    <div className="roulette-pointer"></div>
                                    <div
                                        className="roulette-wheel"
                                        style={{
                                            transform: `rotate(${rotation}deg)`,
                                            transition: isSpinning ? 'transform 4s cubic-bezier(0.15, 0, 0.15, 1)' : 'none',
                                            background: `conic-gradient(from ${-360 / segments.length / 2}deg, ${segments.map((seg, i) => {
                                                const angle = 360 / segments.length;
                                                return `${seg.color} ${i * angle}deg ${(i + 1) * angle}deg`;
                                            }).join(', ')
                                                })`
                                        }}
                                    >
                                        {segments.map((seg, i) => (
                                            <div
                                                key={i}
                                                className="roulette-label-container"
                                                style={{ transform: `rotate(${i * (360 / segments.length)}deg)` }}
                                            >
                                                <div className="roulette-label-text" style={{ color: seg.color === '#1a1a1a' ? '#fff' : '#1a1a1a' }}>
                                                    {seg.text}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="roulette-center-logo">
                                        <img src={`${process.env.PUBLIC_URL}/lellahom_logo.png`} alt="Lellehom" />
                                    </div>
                                </div>

                                {!hasSpun ? (
                                    <button
                                        className={`roulette-btn ${isSpinning ? 'spinning' : ''}`}
                                        onClick={handleSpin}
                                        disabled={isSpinning}
                                    >
                                        {isSpinning ? 'Rotation...' : 'Lancer la roue'}
                                    </button>
                                ) : (
                                    <div className="roulette-result">
                                        <h3>Vous avez obtenu : <span>{result}</span></h3>
                                        <div
                                            className="roulette-code"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => {
                                                navigator.clipboard.writeText(wonCode);
                                                toast.success('Code copié dans le presse-papiers !', { icon: '📋' });
                                            }}
                                            title="Cliquez pour copier"
                                        >
                                            {wonCode} <span style={{ fontSize: '14px', marginLeft: '10px' }}>📋</span>
                                        </div>
                                        <p>Cliquez sur le code pour le copier, et utilisez-le lors de votre commande.</p>
                                        <button className="roulette-btn" onClick={() => setIsOpen(false)}>Continuer mon shopping</button>
                                    </div>
                                )}

                                {!user && !hasSpun && (
                                    <p className="roulette-note">Inscription obligatoire pour participer</p>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default Roulette;
